<template>
  <div class="modal-overlay" v-if="isVisible && !showAddCardModal && !showBillingModal" @click="handleOverlayClick">
    <div class="modal-container" :class="{ 'modal-success': paymentSuccess }" @click.stop>
      <!-- Success State -->
      <div v-if="paymentSuccess" class="success-content">
        <div class="success-icon">
          <img src="@/assets/images/tick-circle.svg"/>
        </div>
        <h3 class="success-title">{{ $t('congrats_success') }}</h3>
        <p class="success-text">{{ $t('addon_success_message') }}</p>
        <button class="done-btn" @click="handleDone">{{ $t('done') }}</button>
      </div>

      <!-- Payment Form -->
      <template v-else>
      <div class="modal-header">
        <h3 class="modal-title">{{ $t('payment_info') }}</h3>
        <div class="close-btn" @click="closeModal">
          <img class="cross" src="@/assets/images/cross.svg" />
        </div>
      </div>

      <div class="modal-body">
        <div class="checkout-layout">
          <!-- Left: Payment Info -->
          <div class="payment-section">
            <div class="section-header">
              <img src="@/assets/images/card_icon.svg" class="section-icon" />
              <h4>{{ $t('payment_info') }}</h4>
            </div>

            <!-- Billing Info -->
            <div class="billing-block">
              <div class="billing-block-header">
                <span class="block-title">{{ $t('billing_info') }}</span>
                <span class="link-btn" @click="openBillingModal">{{ $t('edit') }}</span>
              </div>
              <div v-if="billingInfo" class="billing-details">
                <span>{{ billingInfo.firstName }} {{ billingInfo.lastName }}</span>
                <span>{{ billingInfo.addressLine }}</span>
                <span>{{ billingInfo.phone }}</span>
              </div>
              <div v-else class="billing-empty">
                <span class="link-btn" @click="openBillingModal">{{ $t('add_billing_info') }}</span>
              </div>
            </div>

            <div class="section-divider" />

            <!-- Card Selection -->
            <div class="card-block">
              <div v-if="paymentMethods.length === 0" class="card-empty">
                <span>{{ $t('no_cards_registered') }}</span>
              </div>
              <div v-for="card in paymentMethods" :key="card.cardId" class="card-item" :class="{ selected: selectedCardId === card.cardId }" @click="selectedCardId = card.cardId">
                <div class="card-item-left">
                  <img :src="getCardBrandIcon(card.cardBrand)" class="card-brand-icon" />
                  <div class="card-item-info">
                    <span class="card-number">{{ card.maskedCardNumber }}</span>
                    <span class="card-expiry">{{ String(card.expiryMonth).padStart(2, '0') }}/{{ card.expiryYear }}</span>
                  </div>
                </div>
                <div v-if="selectedCardId === card.cardId" class="card-check">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="8" fill="#4A68F8"/>
                    <path d="M5 8L7 10L11 6" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <span class="link-btn" @click="showAddCardModal = true">{{ $t('add_new_card') }}</span>
            </div>

            <button class="pay-btn" :disabled="!selectedCardId || !billingInfo?.billingId || isProcessing" :class="{ disabled: !selectedCardId || !billingInfo?.billingId || isProcessing }" @click="processPayment">
              {{ isProcessing ? $t('processing') + '...' : $t('complete_payment') }}
            </button>

            <div class="info-note">
              <img src="/src/assets/images/info.svg">
              <span>{{ $t('payment_note') }}</span>
            </div>
          </div>

          <!-- Right: Cart Summary -->
          <div class="summary-section">
            <div class="section-header">
              <img src="@/assets/images/basket.svg" class="section-icon" />
              <h4>{{ $t('cart_summary') }}</h4>
            </div>

            <div class="summary-content">
              <div class="summary-block">
                <span class="summary-block-title">{{ $t('added_packages') }}:</span>
                <div v-if="cartItems.length === 0" class="summary-empty">{{ $t('no_packages_added') }}</div>
                <div v-else class="summary-items">
                  <div v-for="item in cartItems" :key="item.productKey" class="summary-item">
                    <span class="summary-item-name">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M3 7L6 10L11 4" stroke="#10B981" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      {{ item.displayName }} x {{ item.quantity }}
                    </span>
                    <span class="summary-item-price">${{ item.total.toFixed(2) }}</span>
                  </div>
                </div>
              </div>

              <div class="summary-divider" />

              <div class="summary-block">
                <span class="summary-block-title">{{ $t('transaction_summary') }}</span>
                <div class="summary-row">
                  <span>{{ $t('taxes') }}</span>
                  <span>${{ taxes.toFixed(2) }}</span>
                </div>
                <div class="summary-row">
                  <span>{{ $t('discount') }}</span>
                  <span class="discount-value">-${{ discount.toFixed(2) }}</span>
                </div>
                <div class="summary-row">
                  <span>{{ $t('subtotal') }}</span>
                  <span>${{ subtotal.toFixed(2) }}</span>
                </div>
              </div>

              <div class="summary-divider" />

              <div class="summary-total">
                <span>{{ $t('total_amount') }}:</span>
                <span class="summary-total-price">${{ totalAmount.toFixed(2) }}</span>
              </div>

              <div class="info-note">
                <img src="/src/assets/images/info.svg">
                <span>{{ $t('addon_info_text') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      </template>
    </div>
  </div>

  <PricingAddNewCardModal :isVisible="showAddCardModal" @close="showAddCardModal = false" @save="onCardAdded" />
  <PricingInformation :isVisible="showBillingModal" :billingInfo="billingInfo" :mode="billingInfo ? 'update' : 'add'" @close="showBillingModal = false" @billing-updated="onBillingUpdated" />
</template>

<script>
import PricingAddNewCardModal from "@/components/modals/PricingAddNewCardModal.vue";
import PricingInformation from "@/components/modals/PricingInformation.vue";
import pricingService from "@/service/pricingService";
import {handleErrorResponse} from "@/utils/utils";
import visaIcon from '@/assets/images/visa.svg';
import mastercardIcon from '@/assets/images/mastercard.svg';
import cardIcon from '@/assets/images/card.svg';

export default {
  name: 'AddonCheckoutModal',
  components: { PricingAddNewCardModal, PricingInformation },
  props: {
    isVisible: { type: Boolean, default: false },
    cartItems: { type: Array, default: () => [] },
    subtotal: { type: Number, default: 0 },
    taxes: { type: Number, default: 0 },
    discount: { type: Number, default: 0 },
    totalAmount: { type: Number, default: 0 }
  },
  data() {
    return {
      isProcessing: false,
      paymentSuccess: false,
      billingInfo: null,
      paymentMethods: [],
      selectedCardId: null,
      showAddCardModal: false,
      showBillingModal: false
    };
  },
  watch: {
    isVisible(val) {
      if (val) {
        this.paymentSuccess = false;
        this.loadPaymentData();
      }
    }
  },
  methods: {
    closeModal() { this.$emit('close'); },
    handleOverlayClick() {
      if (!this.paymentSuccess) this.closeModal();
    },
    handleDone() {
      this.$emit('payment-success');
      this.closeModal();
    },
    async loadPaymentData() {
      try {
        const [billingResponse, cardsResponse] = await Promise.all([
          pricingService.getBillingInfo(), pricingService.getPaymentMethods()
        ]);
        if (billingResponse.data?.data?.data) {
          const list = billingResponse.data.data.data;
          this.billingInfo = list.find(b => b.isDefault || b.isActive) || list[0] || null;
        }
        if (cardsResponse.data?.data?.data) {
          this.paymentMethods = cardsResponse.data.data.data || [];
          const primary = this.paymentMethods.find(c => c.isPrimary);
          this.selectedCardId = primary ? primary.cardId : (this.paymentMethods[0]?.cardId || null);
        }
      } catch (error) { handleErrorResponse(error, this.$snackbar); }
    },
    getCardBrandIcon(brand) {
      return { visa: visaIcon, mastercard: mastercardIcon }[brand?.toLowerCase()] || cardIcon;
    },
    openBillingModal() { this.showBillingModal = true; },
    async onBillingUpdated() { this.showBillingModal = false; await this.loadPaymentData(); },
    async onCardAdded() { this.showAddCardModal = false; await this.loadPaymentData(); },
    async processPayment() {
      if (!this.billingInfo?.billingId) {
        this.$snackbar?.add({
          text: this.$t('please_add_billing'),
          type: 'error'
        });
        this.openBillingModal();
        return;
      }

      if (!this.selectedCardId) {
        this.$snackbar?.add({
          text: this.$t('please_select_card'),
          type: 'error'
        });
        return;
      }

      this.isProcessing = true;
      try {
        const purchaseData = {
          additionalProducts: this.cartItems.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
          })),
          cardId: this.selectedCardId,
          billingId: this.billingInfo.billingId,
          installment: 0,
        };

        await pricingService.purchaseAddonProducts(purchaseData);
        this.paymentSuccess = true;
      } catch (error) { handleErrorResponse(error, this.$snackbar); }
      finally { this.isProcessing = false; }
    }
  }
};
</script>

<style scoped>
/* Success State */
.success-content {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; text-align: center;
  width: 420px; height: 420px;
}
.success-icon { margin-bottom: 24px; }
.success-title {
  font-size: 22px; font-family: euclid_semi_bold, sans-serif;
  color: #101828; margin: 0 0 8px 0;
}
.success-text {
  font-size: 14px; font-family: euclid_regular, sans-serif;
  color: #667085; margin: 0 0 32px 0; line-height: 1.5; max-width: 320px;
}
.done-btn {
  width: 200px; padding: 14px; border-radius: 8px; border: none;
  background-color: #101828; color: white; font-size: 14px;
  font-family: euclid_medium, sans-serif; cursor: pointer; transition: all 0.2s ease;
}
.done-btn:hover { background-color: #1D2939; }

.modal-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px;
}
.modal-container {
  background: #F9FAFB; border-radius: 16px;
  width: 90vw; max-width: 1000px; max-height: 90vh;
  overflow: hidden; box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  display: flex; flex-direction: column;
}
.modal-container.modal-success {
  width: 420px; max-width: 420px; background: white;
}
.modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-bottom: 1px solid #E5E7EB; flex-shrink: 0;
}
.modal-title { font-size: 18px; font-family: euclid_semi_bold, sans-serif; color: #101828; margin: 0; }
.close-btn {
  background-color: #F2F4F7; border: none; cursor: pointer;
  border-radius: 3000px; display: flex; align-items: center; justify-content: center;
}
.close-btn:hover { background-color: #E4E7EC; }
.cross { padding: 8px; }
.modal-body { flex: 1; overflow-y: auto; padding: 24px; }
.checkout-layout { display: flex; gap: 24px; align-items: flex-start; }

/* Shared */
.section-header { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; }
.section-header h4 { font-size: 16px; font-family: euclid_semi_bold, sans-serif; color: #101828; margin: 0; }
.section-icon { width: 20px; height: 20px; }
.section-divider { height: 1px; background-color: #F2F4F7; margin: 16px 0; }
.link-btn { font-size: 13px; font-family: euclid_medium, sans-serif; color: #4A68F8; cursor: pointer; }
.link-btn:hover { text-decoration: underline; }
.info-note { display: flex; align-items: flex-start; gap: 8px; padding: 10px; background-color: #F9FAFB; border-radius: 8px; margin-top: 16px; }
.info-note svg { flex-shrink: 0; margin-top: 2px; }
.info-note span { font-size: 11px; font-family: euclid_regular, sans-serif; color: #667085; line-height: 1.4; }

/* Left - Payment */
.payment-section {
  flex: 1; background: white; border-radius: 12px;
  padding: 20px; border: 1px solid #E5E7EB;
}
.billing-block { margin-bottom: 0; }
.billing-block-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.block-title { font-size: 14px; font-family: euclid_semi_bold, sans-serif; color: #101828; }
.billing-details { display: flex; flex-direction: column; gap: 4px; }
.billing-details span { font-size: 13px; font-family: euclid_regular, sans-serif; color: #344054; line-height: 1.5; }
.billing-empty { padding: 8px 0; }
.card-block { margin-bottom: 20px; }
.card-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px; border: 1px solid #E5E7EB; border-radius: 10px;
  margin-bottom: 10px; cursor: pointer; transition: all 0.2s ease;
}
.card-item:hover { border-color: #D0D5DD; }
.card-item.selected { border-color: #4A68F8; background-color: #F8F9FF; }
.card-item-left { display: flex; align-items: center; gap: 12px; }
.card-brand-icon { width: 36px; height: 24px; object-fit: contain; }
.card-item-info { display: flex; align-items: center; gap: 16px; }
.card-number { font-size: 13px; font-family: euclid_medium, sans-serif; color: #101828; }
.card-expiry { font-size: 12px; font-family: euclid_regular, sans-serif; color: #667085; }
.card-empty { padding: 12px 0; font-size: 13px; font-family: euclid_regular, sans-serif; color: #98A2B3; }
.pay-btn {
  width: 100%; padding: 14px; border-radius: 8px; border: none;
  background-color: #101828; color: white; font-size: 14px;
  font-family: euclid_medium, sans-serif; cursor: pointer; transition: all 0.2s ease;
}
.pay-btn:hover:not(.disabled) { background-color: #1D2939; }
.pay-btn.disabled { background-color: #E5E7EB; color: #98A2B3; cursor: not-allowed; }

/* Right - Summary */
.summary-section {
  width: 340px; flex-shrink: 0; background: white;
  border-radius: 12px; padding: 20px; border: 1px solid #E5E7EB;
}
.summary-content { display: flex; flex-direction: column; gap: 14px; }
.summary-block-title { font-size: 13px; font-family: euclid_semi_bold, sans-serif; color: #101828; display: block; margin-bottom: 8px; }
.summary-empty { font-size: 12px; font-family: euclid_regular, sans-serif; color: #98A2B3; }
.summary-items { display: flex; flex-direction: column; gap: 8px; }
.summary-item { display: flex; justify-content: space-between; align-items: center; }
.summary-item-name { font-size: 12px; font-family: euclid_regular, sans-serif; color: #344054; display: flex; align-items: center; gap: 6px; }
.summary-item-price { font-size: 12px; font-family: euclid_medium, sans-serif; color: #101828; }
.summary-divider { height: 1px; background-color: #F2F4F7; }
.summary-row { display: flex; justify-content: space-between; align-items: center; padding: 3px 0; }
.summary-row span { font-size: 12px; font-family: euclid_regular, sans-serif; color: #344054; }
.discount-value { color: #10B981 !important; }
.summary-total { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 6px 0; }
.summary-total span:first-child { font-size: 13px; font-family: euclid_medium, sans-serif; color: #344054; }
.summary-total-price { font-size: 24px; font-family: euclid_semi_bold, sans-serif; color: #101828; }

.modal-body::-webkit-scrollbar { width: 6px; }
.modal-body::-webkit-scrollbar-track { background: transparent; }
.modal-body::-webkit-scrollbar-thumb { background-color: rgba(0, 0, 0, 0.15); border-radius: 10px; }

@media (max-width: 768px) {
  .modal-container { width: 100%; max-width: 100%; max-height: 95vh; border-radius: 20px 20px 0 0; }
  .checkout-layout { flex-direction: column; }
  .summary-section { width: 100%; }
}
</style>
